import { useState, useEffect } from "react";
import { Can } from "@/components/Can";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { API } from "@/apiConfig";
import { SubSlotDialog } from "./buttonconfig/SubSlotDialog";
import { AIButtonImageDialog } from "./buttonconfig/AIButtonImageDialog";
import { matchesQuery, findBySku, buildImagePrompt } from "./buttonconfig/buttonConfigUtils";
import { ProductStylePanel } from "./buttonconfig/ProductStylePanel";
import { SubButtonsPanel } from "./buttonconfig/SubButtonsPanel";

// Seeds the AI image "Describe the image" field from Display Name + selected product name —
// combines both when they differ, uses just one when they're the same (or one is blank)
export default function ButtonConfigDialog({ 
  open, 
  onClose, 
  position, 
  currentProduct, 
  currentDisplayName,
  currentCardStyle,
  currentBorderColor,
  currentSubButtons,
  currentButtonImage,
  currentSubButtonsImage,
  tabId,
  allProducts,
  categories = [],
  subCategories = [],
  groups = [],
  buttonPositions = [],
  employee,
  onSave,
  onEditProduct
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cardStyle, setCardStyle] = useState("default");
  const [borderColor, setBorderColor] = useState("default");
  const [activeSection, setActiveSection] = useState("product"); // "product" or "sub-buttons"
  const [subButtons, setSubButtons] = useState([]); // [{position, product_id, label, color}]
  const [subSearchQuery, setSubSearchQuery] = useState("");
  const [editingSlot, setEditingSlot] = useState(null); // which slot is being configured
  const [buttonImage, setButtonImage] = useState(""); // Product & Style tab image — 1st priority
  const [subButtonsImage, setSubButtonsImage] = useState(""); // Sub-Buttons tab image — 2nd priority
  const [imageUrlInput, setImageUrlInput] = useState("");
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [aiDialogTarget, setAiDialogTarget] = useState("product"); // which tab opened the AI dialog
  // AI image generation is a backend call served by the Primary's backend, so it works from
  // every terminal (Primary, Client, Back Office). Gated by role only.
  const canGenerateAI = ["owner", "admin", "manager"].includes(employee?.role);

  // Shared "Upload" handler for both tabs — on a real Windows build, opens Electron's native
  // file picker starting in the same local btnimages folder every approved/AI image already
  // lives in (window.electron.pickButtonImage), so uploads and past images live side by side.
  // In a plain browser (web Back Office) there's no way to control that starting folder, so
  // it falls back to a normal <input type=file>. Either way, the picked file is sent through
  // the SAME /button-images/approve endpoint the AI flow uses — saved as a real local file
  // (not a giant base64 blob in Mongo) and pushed to Central for backup.
  const pickAndUploadButtonImage = async (setTargetImage) => {
    let dataUrl;
    if (window.electron?.pickButtonImage) {
      dataUrl = await window.electron.pickButtonImage();
    } else {
      dataUrl = await new Promise((resolve) => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.onchange = (e) => {
          const file = e.target.files?.[0];
          if (!file) return resolve(null);
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(file);
        };
        input.click();
      });
    }
    if (!dataUrl) return;
    try {
      const authHeader = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const res = await axios.post(`${API}/button-images/approve`, { image_base64: dataUrl }, { headers: authHeader });
      setTargetImage(res.data.image_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    }
  };

  // Get product IDs already assigned to other positions on this tab
  const usedProductIds = new Set(
    buttonPositions
      .filter(bp => bp.tab_id === tabId && bp.position !== position && bp.product_id)
      .map(bp => bp.product_id)
  );

  useEffect(() => {
    if (open) {
      setSelectedProduct(currentProduct);
      setDisplayName(currentDisplayName || currentProduct?.name || "");
      setCardStyle(currentCardStyle || "default");
      setBorderColor(currentBorderColor || "default");
      setSubButtons(currentSubButtons || []);
      setButtonImage(currentButtonImage || "");
      setSubButtonsImage(currentSubButtonsImage || "");
      setImageUrlInput("");
      setActiveSection("product");
      setEditingSlot(null);
      setSubSearchQuery("");
    }
  }, [open, currentProduct, currentDisplayName, currentCardStyle, currentBorderColor, currentSubButtons, currentButtonImage, currentSubButtonsImage]);

  const filteredProducts = searchQuery.trim().length >= 3
    ? allProducts.filter(product => matchesQuery(product, searchQuery.trim()) && !usedProductIds.has(product.id)).slice(0, 50)
    : [];

  const pickProduct = (product) => {
    setSelectedProduct(product);
    if (!displayName || displayName === (currentProduct?.name || "")) {
      setDisplayName(product.name);
    }
  };

  const handleSearchKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const exact = findBySku(allProducts, searchQuery);
    if (exact) {
      if (usedProductIds.has(exact.id)) { toast.error("This product is already on another button of this tab"); return; }
      pickProduct(exact);
      setSearchQuery("");
      toast.success(`Selected ${exact.name}`);
    } else if (filteredProducts.length === 1) {
      pickProduct(filteredProducts[0]);
      setSearchQuery("");
    } else if (searchQuery.trim()) {
      toast.error("No product with that SKU");
    }
  };

  const handleSave = async () => {
    const hasSubButtons = subButtons.length > 0;
    if (!selectedProduct && !hasSubButtons) {
      toast.error("Please select a product or add sub-buttons");
      return;
    }
    if (!selectedProduct && hasSubButtons && !displayName.trim()) {
      toast.error("Display name is required for sub-button groups");
      return;
    }

    const token = localStorage.getItem("token");
    try {
      await axios.put(
        `${API}/button-positions/${tabId}/${position}`,
        {
          product_id: selectedProduct?.id || null,
          display_name: selectedProduct ? (displayName !== selectedProduct.name ? displayName : null) : displayName.trim(),
          card_style: cardStyle === "default" ? null : cardStyle,
          border_color: borderColor === "default" ? null : borderColor,
          sub_buttons: hasSubButtons ? subButtons : null,
          button_image: buttonImage || null,
          sub_buttons_image: subButtonsImage || null
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success("Button configured successfully");
      onSave();
      onClose();
    } catch (error) {
      toast.error("Failed to configure button");
    }
  };

  const handleClear = async () => {
    const token = localStorage.getItem("token");
    try {
      await axios.delete(`${API}/button-positions/${tabId}/${position}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Button cleared");
      onSave();
      onClose();
    } catch (error) {
      toast.error("Failed to clear button");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="button-config-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">
            Configure Button (Position {position + 1})
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-1 border-b border-zinc-200 pb-2">
          <button onClick={() => setActiveSection("product")}
            data-testid="button-config-product-tab"
            className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${activeSection === "product" ? "bg-blue-600 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
            Product & Style
          </button>
          <Can k="products.button_config.sub_buttons"><button onClick={() => setActiveSection("sub-buttons")}
            data-testid="button-config-sub-buttons-tab"
            className={`px-3 py-1 text-xs font-medium rounded-md transition-all ${activeSection === "sub-buttons" ? "bg-blue-600 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}>
            Sub-Buttons ({subButtons.length})
          </button></Can>
        </div>

        {activeSection === "product" ? (
          <ProductStylePanel
            displayName={displayName} setDisplayName={setDisplayName}
            buttonImage={buttonImage} setButtonImage={setButtonImage}
            imageUrlInput={imageUrlInput} setImageUrlInput={setImageUrlInput}
            canGenerateAI={canGenerateAI} setShowAIDialog={() => { setAiDialogTarget("product"); setShowAIDialog(true); }}
            cardStyle={cardStyle} setCardStyle={setCardStyle}
            borderColor={borderColor} setBorderColor={setBorderColor}
            searchQuery={searchQuery} setSearchQuery={setSearchQuery} handleSearchKeyDown={handleSearchKeyDown}
            selectedProduct={selectedProduct} filteredProducts={filteredProducts}
            categories={categories} subCategories={subCategories} groups={groups}
            pickProduct={pickProduct} onEditProduct={onEditProduct}
            handleSave={handleSave} currentProduct={currentProduct} handleClear={handleClear}
            onClose={onClose} subButtons={subButtons}
            onUploadImage={() => pickAndUploadButtonImage(setButtonImage)}
          />
        ) : (
          <SubButtonsPanel
            subButtons={subButtons} allProducts={allProducts}
            setEditingSlot={setEditingSlot} setSubSearchQuery={setSubSearchQuery}
            selectedProduct={selectedProduct}
            buttonImage={subButtonsImage} setButtonImage={setSubButtonsImage}
            imageUrlInput={imageUrlInput} setImageUrlInput={setImageUrlInput}
            canGenerateAI={canGenerateAI} setShowAIDialog={() => { setAiDialogTarget("sub-buttons"); setShowAIDialog(true); }}
            handleSave={handleSave} onClose={onClose}
            onUploadImage={() => pickAndUploadButtonImage(setSubButtonsImage)}
          />
        )}

        {/* Sub-Button Slot Config Dialog */}
        {editingSlot !== null && (
          <SubSlotDialog
            slot={editingSlot}
            current={subButtons.find(s => s.position === editingSlot) || null}
            allProducts={allProducts}
            employee={employee}
            onSave={(slotData) => {
              setSubButtons(prev => {
                const filtered = prev.filter(s => s.position !== editingSlot);
                return [...filtered, { position: editingSlot, ...slotData }];
              });
              setEditingSlot(null);
            }}
            onClear={() => {
              setSubButtons(prev => prev.filter(s => s.position !== editingSlot));
              setEditingSlot(null);
            }}
            onClose={() => setEditingSlot(null)}
          />
        )}

        {/* AI Button Image Generation Dialog */}
        {showAIDialog && (
          <AIButtonImageDialog
            open={showAIDialog}
            initialPrompt={buildImagePrompt(displayName, selectedProduct?.product_name || selectedProduct?.name)}
            onClose={() => setShowAIDialog(false)}
            onApprove={(url) => { (aiDialogTarget === "sub-buttons" ? setSubButtonsImage : setButtonImage)(url); setShowAIDialog(false); }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
